import { useState } from "react";

import {
  type Interface,
  type Member,
  useFetchConnectedAssets,
  useSearchInterfaceCollaborators,
} from "@zapier/client";
import { ChangeOwnerModal } from "@zapier/shared";
import { AssetTypes } from "@zapier/utils";

import { useTransferInterface } from "./useTransferInterface";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  interfaceObj: Interface | undefined;
  preselectedUser?: Member;
  folderId?: string; // used for cache invalidation in mutations
}

const InterfaceChangeOwnerModal = ({
  isOpen,
  onClose,
  interfaceObj,
  preselectedUser,
  folderId,
}: Props) => {
  const [search, setSearch] = useState("");

  const {
    collaborators,
    isLoading: isLoadingCollaborators,
    isFetching: isFetchingCollaborators,
  } = useSearchInterfaceCollaborators({
    id: interfaceObj?.id || "",
    search,
  });

  const {
    connectedAssets,
    isLoading: isLoadingConnectedAssets,
    isFetching: isFetchingConnectedAssets,
  } = useFetchConnectedAssets({ id: interfaceObj?.id || "" });

  const { transferInterface, isSubmitting } = useTransferInterface({
    interface: interfaceObj,
    folderId,
  });

  const handleSubmit = async (ownerId: string) => {
    const userId = preselectedUser?.id || Number(ownerId);
    if (userId) {
      transferInterface(userId);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
      setSearch("");
    }
  };

  if (!interfaceObj) {
    return;
  }

  return (
    <ChangeOwnerModal
      id={interfaceObj.id}
      title={interfaceObj.name}
      currentOwnerId={interfaceObj.customuserId}
      linkedAssets={connectedAssets}
      isLoading={isLoadingConnectedAssets || isFetchingConnectedAssets}
      isOpen={isOpen}
      setIsOpen={handleOpenChange}
      users={collaborators}
      assetType={AssetTypes.Interface}
      onSearch={setSearch}
      isUsersLoading={isLoadingCollaborators || isFetchingCollaborators}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      preselectedUser={preselectedUser}
    />
  );
};

export default InterfaceChangeOwnerModal;
